import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import ModeIcon from '#/trip-planner/components/ModeIcon';
import VehicleIcon from '#/shared/components/VehicleIcon';
import { RentalCompanyIconWithStyles } from '#/shared/components/RentalCompanyIcon';
import GTM_HANDLES from '#/shared/gtm-handles';
import useIntlOtpUiConfig from '#/trip-planner/hooks/useIntlOtpUiConfig';
import * as Styled from './styled';

function ModeSelector({
  queryParams,
  onQueryParamChange,
  onPlanTrip,
  onSaveSettings,
  modeOverlayVisible,
  setModeOverlayVisible,
  closeModeOverlay,
}) {
  const { modes, companies } = useIntlOtpUiConfig();

  return (
    <Styled.ModeSelectorLayout>
      <Styled.TripOptions
        supportedModes={modes}
        supportedCompanies={companies}
        queryParams={queryParams}
        onQueryParamChange={onQueryParamChange}
        QuestionIcon={<Styled.InfoIcon />}
        SimpleModeIcon={ModeIcon}
        DetailedModeIcon={VehicleIcon}
        CompanyIcon={RentalCompanyIconWithStyles({
          preferTextBased: true,
          ...Styled.rentalCompanyIconStyles,
        })}
        featuredItemOverlayShown={setModeOverlayVisible}
        featuredItemOverlayEnabled={!closeModeOverlay}
        featureItemOverlayBackButton={false}
      />
      {!modeOverlayVisible && (
        <Styled.ModeSelectorButtonContainer>
          {!!queryParams.from && !!queryParams.to && (
            <Styled.PlanTripButton
              variant="primary"
              onClick={onPlanTrip}
              dataGtm={GTM_HANDLES.shared.plannerOptionsGetDirectionsButton}
            >
              <FormattedMessage id="planner.plantrip" />
            </Styled.PlanTripButton>
          )}
          <Styled.SaveSettingsButton onClick={onSaveSettings}>
            <FormattedMessage id="planner.savechanges" />
          </Styled.SaveSettingsButton>
        </Styled.ModeSelectorButtonContainer>
      )}
    </Styled.ModeSelectorLayout>
  );
}

ModeSelector.propTypes = {
  /** Contains the URL state that allows a user to recreate a trip */
  queryParams: PropTypes.shape({
    to: PropTypes.shape({}),
    from: PropTypes.shape({}),
  }).isRequired,
  /** Contains all of the modes that are used in the Options panel */
  onQueryParamChange: PropTypes.func.isRequired,
  /** Plans trip with current settings when clicked */
  onPlanTrip: PropTypes.func.isRequired,
  /** Navigates back to trip Planner when clicked */
  onSaveSettings: PropTypes.func.isRequired,
  /**
   * State node indicating whether the mode overlay in the TripOptions component
   * is visible
   */
  modeOverlayVisible: PropTypes.bool.isRequired,
  /**
   * Setter for `modeOverlayVisible` state node, used by TripOptions to keep the
   * overlay visibility value up-to-date
   */
  setModeOverlayVisible: PropTypes.func.isRequired,
  /** If set to `false` the mode overlay in TripOptions will be closed */
  closeModeOverlay: PropTypes.bool.isRequired,
};

export default ModeSelector;
